import React, { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getFixedHeight, showError, fetchWithRetry } from '../../utils';
import { getItems } from '../../api'; // Funzione generica per chiamate API

export default function Vista({
    // API e dati
    apiCallEndpoint,
    initialFilterParams = {},
    
    // Componenti personalizzabili
    FilterBarComponent,
    ListRowComponent,
    
    // Configurazione UI
    actions = [],
    fullSizeActions = [],
    selectable = false,
    
    // Messaggi personalizzabili
    loadingMessage = "Caricamento in corso…",
    endMessage = "Non ci sono altri elementi",
    
    // Configurazione paginazione
    pageSize = 20,
    
    // Callback per selezione (se selectable = true)
    onSelectionChange,
    
    // Transform per raggruppamento (opzionale, per CallLog)
    groupTransform,
    
    // Funzione per processare i parametri prima della chiamata API (opzionale)
    processParams
}) {
    const [filterParams, setFilterParams] = useState(initialFilterParams);
    const [debouncedParams, setDebouncedParams] = useState(filterParams);
    const [rawList, setRawList] = useState([]);
    const [selectedList, setSelectedList] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    
    // Debounce per i parametri di ricerca
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedParams(filterParams);
        }, 500);

        return () => clearTimeout(timer);
    }, [filterParams]);

    // Reset quando cambiano i parametri
    useEffect(() => {
        setRawList([]);
        setPage(1);
        setHasMore(true);
    }, [debouncedParams]);

    // Chiamata API
    useEffect(() => {
        let ignore = false;//da react 18 lo strict mode chiama lo useEffect due volte,
                           //quindi in caso di rerendering devo ignorare tutte le chiamate a parte l'ultima
        

        let params = { 
            pagination: { page, page_count: pageSize },
            ...debouncedParams
        };

        if (processParams) {
            params = processParams(params);
        }

        async function fetchData() {
            const res = await getItems(apiCallEndpoint, params);
            if (!ignore) {
                setRawList(prev => [...prev, ...res.list]);
                setHasMore(page < res.pagination.total_pages);
            }
        }

        fetchWithRetry(fetchData, ignore)
        .catch(err => {
            if (!ignore) {
                showError([err.message]);
            }
        });

        return () => {
            ignore = true;
        };
    }, [page, debouncedParams, apiCallEndpoint, pageSize]);

    // Gestione selezione
    const handleSelect = (itemId) => {
        if (!selectable) return;
        
        setSelectedList(prev => {
            const isSelected = prev.includes(itemId);
            const newSelection = isSelected 
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId];
            
            if (onSelectionChange) {
                const selectedItems = rawList.filter(item => newSelection.includes(item.id));
                onSelectionChange(newSelection, selectedItems);
            }
            
            return newSelection;
        });
    };

    const loadMore = () => { 
        if (hasMore) setPage(prev => prev + 1); 
    };

    // Applica trasformazione per raggruppamento se presente
    const processedList = groupTransform ? groupTransform(rawList) : rawList;
    const isGrouped = Array.isArray(processedList) && processedList[0]?.items;

    // Calcola altezza scroll
    const getScrollHeight = () => {
        return `calc(100dvh - ${getFixedHeight()}px)`;
    };

    return (
        <div className="vista">
            {(actions.length > 0 || fullSizeActions.length > 0 || FilterBarComponent) && (
                <div className="vista-header">
                     {fullSizeActions.map((action, index) => (
                        <button 
                            key={index}
                            className={"submit fullsize-action " + (action.className || "add-btn")}
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                          {action.label}
                        </button>
                    ))}
                    {FilterBarComponent && (
                        <FilterBarComponent 
                            filterParams={filterParams} 
                            onChange={setFilterParams} 
                        />
                    )}
                    {actions.map((action, index) => (
                        <button 
                            key={index}
                            className={"icon-button " + (action.className || "add-btn")}
                            onClick={action.onClick}
                        >
                            {action.icon && <span className="icon">{action.icon}</span>}
                        </button>
                    ))}
                </div>
            )}
            
            <div className="vista-list">
                <InfiniteScroll
                    className="vista-infinite-scroll"
                    dataLength={rawList.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={<p className="infinite-loading">{loadingMessage}</p>}
                    endMessage={<p className="infinite-end">{endMessage}</p>}
                    scrollableTarget="vista-list"
                    height={getScrollHeight()}
                >
                    {isGrouped ? (
                        // Rendering per liste raggruppate (CallLog)
                        processedList.map(group => (
                            <div key={group.day} className="day-group">
                                <div className="day-separator">{group.label}</div>
                                <div className="vista-list">
                                    {group.items.map((item, key) => (
                                        <ListRowComponent 
                                            key={item.id + "-" + key}
                                            itemGroup={item}
                                            setRawList={setRawList}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        // Rendering per liste semplici
                        <div className="vista-list">
                            {processedList.map((item, key) => (
                                <ListRowComponent 
                                    key={item.id + "-" + key}
                                    contact={item}
                                    setRawList={setRawList}
                                    selectedList={selectable ? selectedList : undefined}
                                    handleSelectContact={selectable ? handleSelect : undefined}
                                />
                            ))}
                        </div>
                    )}
                </InfiniteScroll>
            </div>
        </div>
    );
}